import React, { useState, useEffect, FC } from "react";
import { Select, Button, Text, Group, Loader, TextInput, LoadingOverlay } from "@mantine/core";
import { FileType, LevelType, SubjectType } from "src/interfaces/KnowledgeBase";
import { knowledgeBaseService } from "src/services/services/knowledgeBase/knowledgeBase.api";
import Icon from "../common/Icons/Icons/Icon";
import { ManualUploadKnowledgeBaseEditor } from "./ManualUploadForm/ManualUploadKnowledgeBaseEditor";
import { useManualDocsUpload } from "src/hooks/useManualKnowledgeBaseDocsUpload";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/redux/redux-store/configureStore";
import {
  setFormFields,
  setCurrentStep,
  setIsLoading,
  startParsing,
  parseSuccess,
  parseError,
  resetForm,
  resetFileName,
} from "src/redux/redux-store/knowledge-base/knowledgebaseReducer";
import { useKnowledgebase } from "src/hooks/useKnowledgebase";
import { useQuestionForm } from "src/hooks/useManualQuestionBankDocsUpload";
import { QBQuestionFormEditor } from "./ManualUploadForm/ManualUploadQuestionBankEditor";
import { useBackgroundNotifications } from "src/hooks/useBackgroundNotifications";

const FILE_SIZE_LIMIT = 15000000;

interface AutoUploadFormProps {
  onSuccess?: (message?: string) => void;
  onError?: (error: string) => void;
  sharedLevels?: LevelType[];
  isLoadingLevels?: boolean;
}

const AutoUploadForm: FC<AutoUploadFormProps> = ({ onSuccess, onError, sharedLevels = [], isLoadingLevels = false }) => {
  // Local state for non-serializable data (the File object)
  const [file, setFile] = useState<File | null>(null);
  const [subjects, setSubjects] = useState<SubjectType[]>([]);
  const [levels, setLevels] = useState<LevelType[]>(sharedLevels);
  const [error, setError] = useState<string | null>(null);

  // Redux state
  const dispatch = useDispatch<AppDispatch>();
  const {
    fileName,
    type,
    level,
    subject,
    hasUploadedFile,
    currentStep,
    isLoading,
    fileBeingParsed,
  } = useSelector((state: RootState) => state.knowledgebase);

  const {
    themes,
    config,
    addTheme,
    removeTheme,
    updateThemeName,
    addTopic,
    removeTopic,
    updateTopicName,
    addLearningObjective,
    removeLearningObjective,
    updateLearningObjectiveName,
    updateContent,
    prefillForm,
    validate,
    getFormData,
  } = useManualDocsUpload();

  const {
    getCombinedSummary,
    getUrlsToPostOnAzure,
    loading: kbLoading,
    uploadFilesToAzure,
    fetchingCombinedSummary,
  } = useKnowledgebase(false);

  const {
    qbThemes,
    prefillQBForm,
    transformedQBData,
    getAllNewImagesForEditCase,
    updateThemesWithFileUrls,
    buildStructuredQBThemes,
    updateSelectedTheme,
    updateSelectedTopic,
    validateQBForm,
    resetQBForm,
    getQBFormData,
    setQBConfigFromStructure,
    addQBTheme,
    removeQBTheme,
    addQBTopic,
    removeQBTopic,
    addQBQuestion,
    removeQBQuestion,
    updateQBQuestion,
    createTransformedQB,
    qbConfig,
  } = useQuestionForm();

  const { showPlaceholderDrawer } = useBackgroundNotifications();

  useEffect(() => {
    const fetchSubjectsForLevel = async () => {
      if (!level) {
        setSubjects([]);
        return;
      }
      try {
        dispatch(setIsLoading(true));
        const response = await knowledgeBaseService.getSubjects(Number(level));
        if (response.kind === "ok") {
          setSubjects(response.data);
        } else {
          throw new Error("Failed to load subjects for selected level");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load subjects";
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        dispatch(setIsLoading(false));
      }
    };

    fetchSubjectsForLevel();
  }, [level, onError]);

  // Update levels when sharedLevels prop changes
  useEffect(() => {
    if (sharedLevels.length > 0) {
      setLevels(sharedLevels);
    }
  }, [sharedLevels]);

  useEffect(() => {
    console.log("=== AUTO UPLOAD USEEFFECT TRIGGERED ===");
    console.log("praseSuccess:", fileBeingParsed.praseSuccess);
    console.log("parseSuccessResponse:", fileBeingParsed.parseSuccessResponse);
    console.log("type:", type);

    if (fileBeingParsed.praseSuccess && fileBeingParsed.parseSuccessResponse) {
      console.log("Both conditions met, proceeding...");
      if (type === "Question Bank") {
        console.log("Type is Question Bank, fetching combined summary...");
        const fetchCombinedSummary = async () => {
          const levelName = levels.find((l) => l.id.toString() === level)!.name;
          console.log("Fetching combined summary for:", levelName, subject?.name);
          const { data, error } = await getCombinedSummary(
            levelName,
            subject?.name
          );
          console.log("Combined summary result:", { data, error });
          if (data) {
            console.log("Creating transformed QB...");
            const { newQbData } = createTransformedQB(data);
            console.log("Calling prefillQBForm with:", { parseResponse: fileBeingParsed.parseSuccessResponse, newQbData });
            prefillQBForm(fileBeingParsed.parseSuccessResponse, newQbData);
          } else {
            setError(error);
          }
        };
        level && levels.length > 0 && subject && fetchCombinedSummary();
      } else {
        prefillForm(JSON.stringify(fileBeingParsed.parseSuccessResponse));
      }
    }
  }, [
    fileBeingParsed.praseSuccess,
    fileBeingParsed.parseSuccessResponse,
    level,
    levels,
    subject,
  ]);

  const isLoadingSomething =
    kbLoading ||
    isLoading ||
    fileBeingParsed.isBeingParsed ||
    fetchingCombinedSummary;

  const handleSubjectChange = (value: string | null) => {
    if (!value) {
      dispatch(setFormFields({ fileName, type, level, subject: null }));
      return;
    }
    const selectedSubject = subjects.find((s) => s.id.toString() === value);
    if (selectedSubject) {
      dispatch(
        setFormFields({
          fileName,
          type,
          level,
          subject: selectedSubject,
        })
      );
      setError(null);
    }
  };

  const handleLevelChange = (value: string | null) => {
    if (value) {
      dispatch(
        setFormFields({
          fileName,
          type,
          level: value,
          subject: null,
        })
      );
    }
  };

  const handleTypeChange = (value: FileType) => {
    dispatch(
      setFormFields({
        fileName,
        type: value,
        level,
        subject,
      })
    );
    setError(null);
  };

  const renderMainFields = () => (
    <>
      <div className="space-x-4 flex relative">
        <LoadingOverlay visible={isLoadingLevels} overlayBlur={2} />
        <Select
          label="File Type"
          size="xs"
          placeholder="Select file type"
          data={[
            { value: "Question Bank", label: "Question Bank (QB)" },
            { value: "Knowledge", label: "Knowledge Base (KB)" },
          ].map((item) => ({
            ...item,
            value: item.value as FileType,
          }))}
          value={type}
          onChange={handleTypeChange}
          required
          disabled={
            currentStep !== 1 || isLoading || fileBeingParsed.isBeingParsed || isLoadingLevels
          }
        />
        <Select
          label="Level"
          size="xs"
          placeholder={isLoadingLevels ? "Loading levels..." : "Select a level"}
          data={levels.map((l) => ({
            value: l.id.toString(),
            label: l.name,
          }))}
          value={level}
          onChange={handleLevelChange}
          required
          disabled={
            isLoading || currentStep !== 1 || fileBeingParsed.isBeingParsed || isLoadingLevels
          }
        />
        <Select
          label="Subject"
          size="xs"
          placeholder={isLoading ? "Fetching subjects..." : "Select a subject"}
          data={subjects.map((s) => ({
            value: s.id.toString(),
            label: s.name,
          }))}
          value={subject?.id?.toString() || null}
          onChange={handleSubjectChange}
          required
          disabled={
            isLoading || currentStep !== 1 || fileBeingParsed.isBeingParsed || isLoadingLevels
          }
        />
      </div>
    </>
  );

  const renderFileUpload = () => (
    <>
      {(hasUploadedFile && fileBeingParsed.isBeingParsed) ||
        currentStep === 2 ? (
        <div className="flex items-end gap-2 mt-2">
          <TextInput
            label="File Name"
            size="xs"
            className="flex-1"
            value={fileName || ""}
            required
            disabled
          />
          <Button
            size="xs"
            onClick={handleFileReset}
            className="mt-2"
            disabled={
              isLoading ||
              fileBeingParsed.isBeingParsed ||
              fileBeingParsed.praseSuccess
            }
          >
            Change File
          </Button>
        </div>
      ) : (
        <div className="space-y-4 mt-6">
          <Text size="xs" fw={500}>
            Upload File
          </Text>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            {file ? (
              <div className="space-y-2">
                <Text size="sm">{file.name}</Text>
                <Text size="xs" c="dimmed">
                  {(file.size / 1024).toFixed(2)} KB
                </Text>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    setFile(null);
                    dispatch(resetFileName());
                  }}
                  className="mt-2"
                  disabled={isLoading}
                >
                  Change File
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-center">
                  <Icon name="upload" size="24px" className="mr-1" />
                </div>
                <div>
                  <label className="cursor-pointer">
                    <Text fw={500} size="sm">
                      Click to upload
                    </Text>
                    <Text size="xs" c="dimmed">
                      or drag and drop
                    </Text>
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) =>
                        handleFileUpload(e.target.files?.[0] || null)
                      }
                      accept=".pdf"
                      disabled={isLoading}
                    />
                  </label>
                </div>
                <Text size="xs" c="dimmed">
                  PDF (max 15MB)
                </Text>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  const renderFormFields = () => {
    return (
      <div className="flex flex-col">
        <div className="py-2">
          {renderMainFields()}
          {renderFileUpload()}
        </div>
        {currentStep === 2 && (
          <>
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-8 space-y-4">
                <Loader size="md" />
                <Text>Processing file...</Text>
              </div>
            ) : hasUploadedFile ? (
              <div className="overflow-y-auto max-h-[calc(100vh-520px)] lg:max-h-[calc(100vh-420px)]">
                {type === "Knowledge" && themes ? (
                  <ManualUploadKnowledgeBaseEditor
                    data={{
                      themes,
                      config,
                      addTheme,
                      removeTheme,
                      updateThemeName,
                      addTopic,
                      removeTopic,
                      updateTopicName,
                      addLearningObjective,
                      removeLearningObjective,
                      updateLearningObjectiveName,
                      updateContent,
                    }}
                  />
                ) : null}

                {type === "Question Bank" && qbThemes ? (
                  <QBQuestionFormEditor
                    data={{
                      isEditing: true,
                      qbThemes,
                      transformedQBData,
                      qbConfig,
                      addQBTheme,
                      removeQBTheme,
                      addQBTopic,
                      removeQBTopic,
                      addQBQuestion,
                      removeQBQuestion,
                      updateQBQuestion,
                      validateQBForm,
                      resetQBForm,
                      getQBFormData,
                      setQBConfigFromStructure,
                      createTransformedQB,
                      updateSelectedTheme,
                      updateSelectedTopic,
                    }}
                  />
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  };

  const handleFileReset = () => {
    dispatch(resetForm());
    setFile(null);
    setError(null);
  };

  const handleFileUpload = (selectedFile: File | null) => {
    if (!selectedFile) return;

    if (selectedFile.size > FILE_SIZE_LIMIT) {
      setError("File size exceeds 15MB limit");
      return;
    }

    setFile(selectedFile);
    dispatch(
      setFormFields({
        fileName: selectedFile.name,
        type,
        level,
        subject,
      })
    );
    setError(null);
  };

  const handleFileParsing = async () => {
    const levelName = levels.find((l) => l.id.toString() === level)?.name;
    if (!levelName) {
      setError("Level not found");
      return;
    }
    try {
      if (!file) {
        throw new Error("No file selected.");
      }
      dispatch(startParsing());
      const response = await knowledgeBaseService.parseFile(
        file,
        levelName,
        subject!.name,
        type === "Question Bank" ? true : false
      );
      console.log("Parse response:", response);
      if (response.kind === "ok") {
        console.log("Dispatching parseSuccess with data:", response.data);
        dispatch(parseSuccess(response.data));
        console.log("Parse success dispatched");
        onSuccess?.();
        dispatch(setCurrentStep(2));
      } else {
        throw new Error(response.message || "Failed to parse file");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to parse file";
      onError?.(errorMessage);
      dispatch(parseError());
    }
  };

  const handleFileUploading = async (formData: string) => {
    if (!fileBeingParsed.fileName) {
      setError("No file selected.");
      return;
    }
    const levelName = levels.find((l) => l.id.toString() === level)?.name;
    if (!levelName) {
      setError("Level not found");
      return;
    }
    try {
      dispatch(setIsLoading(true));
      const response = await knowledgeBaseService.uploadManualFile(
        type === "Question Bank" ? true : false,
        levelName,
        subject!.name,
        formData,
        file?.name || "",
        true
      );
      if (response.kind !== "ok") {
        throw new Error(response.message || "Failed to upload file");
      }
      // Show drawer immediately with placeholder
      showPlaceholderDrawer();
      onSuccess?.();
      handleFileReset();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to upload file";
      onError?.(errorMessage);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  // auto upload QB starts here

  const getImageUrls = async () => {
    const fileMap = getAllNewImagesForEditCase();
    const fileUUIDs = Object.keys(fileMap);
    if (!fileUUIDs.length) return;

    const imageUrls = await getUrlsToPostOnAzure(fileName!, fileUUIDs);
    if (!imageUrls) return;

    // Merge signed URLs with files into a single map
    const uploadMap = Object.fromEntries(
      fileUUIDs.map((uuid) => [
        uuid,
        {
          uploadUrl: imageUrls[uuid].upload_url, // assuming your API returns this
          file: fileMap[uuid],
        },
      ])
    );
    const readUrls = Object.fromEntries(
      fileUUIDs.map((uuid) => [uuid, imageUrls[uuid].read_url]) // ensure backend provides this
    );
    return { uploadMap, readUrls };
  };

  const handleQBFileUploading = async () => {
    const result = await getImageUrls();
    let updatedThemes = qbThemes;
    if (result) {
      const { uploadMap, readUrls } = result;
      const uploadResult = await uploadFilesToAzure(uploadMap);
      if (uploadResult.kind !== "ok") {
        setError("Image uploading failed!");
        return;
      }
      updatedThemes = updateThemesWithFileUrls(readUrls);
    }

    // Always build and send the structured QB themes
    const buildStructuredQBThemesResult =
      buildStructuredQBThemes(updatedThemes);
    await handleFileUploading(buildStructuredQBThemesResult);
  };

  const fetchCombinedSummary = async () => {
    const levelName = levels.find((l) => l.id.toString() === level)!.name;
    const { data, error } = await getCombinedSummary(levelName, subject?.name);
    if (data) {
      createTransformedQB(data);
    } else {
      setError(error);
    }
  };

  const handleQBFileParsing = async () => {
    await fetchCombinedSummary();
    await handleFileParsing();
  };

  // auto upload QB ends here

  const handleStepOne = async () => {
    if (type === "Question Bank") {
      await handleQBFileParsing();
    } else {
      await handleFileParsing();
    }
  };

  const handleStepTwo = async () => {
    if (type === "Question Bank") {
      await handleQBFileUploading();
    } else {
      const formData = getFormData();
      console.log("I DID ", formData, JSON.stringify(formData));
      await handleFileUploading(JSON.stringify(formData));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (currentStep === 1) {
        await handleStepOne();
      } else {
        await handleStepTwo();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Upload failed";
      setError(errorMessage);
      onError?.(errorMessage);
    }
  };

  const isFormInvalid =
    !fileName ||
    !subject ||
    !level ||
    !type ||
    (currentStep === 2 &&
      (type === "Question Bank" ? !validateQBForm() : !validate())) ||
    isLoadingSomething;

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="space-y-6 pr-3"
      >
        {renderFormFields()}

        {error && (
          <Text color="red" size="sm" mt="sm">
            {error}
          </Text>
        )}
        <div className="flex flex-col gap-2 lg:flex-row lg:justify-between mt-2 pb-6">
          <Button variant="default" onClick={handleFileReset} className="w-full lg:w-auto">
            Reset
          </Button>
          <div className="flex flex-col gap-2 lg:flex-row">
            {currentStep === 2 && (
              <Button
                variant="default"
                onClick={() => dispatch(setCurrentStep(1))}
                disabled={isLoadingSomething}
                className="w-full lg:w-auto"
              >
                Back
              </Button>
            )}
            <Button
              type="submit"
              loading={isLoadingSomething}
              disabled={isFormInvalid}
              className="w-full lg:w-auto"
            >
              {currentStep === 1 ? "Next" : "Upload"}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default AutoUploadForm;
